// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import MultiSelect from '../controls/MultiSelect'
import Select from '../controls/Select'
import Action from './Action'
import Conditions from './Conditions'
import styled, { type StyledComponent } from 'styled-components'
import type { OptionType, GroupedOptionType, ConditionType, ActionType } from '../types'
import ErrorText from '../controls/ErrorText'
import { alertColor } from '../controls/colors'
import FloatPanel from '../controls/FloatPanel'

const FormWrapper: StyledComponent<Object, any, any> = styled.div`
  width: 100%;
`

const FormHeader: StyledComponent<Object, any, any> = styled.div`
  font-size: 28px;
  font-weight: bold;
  padding-bottom: 16px;
`

const InputGroupWrapper: StyledComponent<Object, any, any> = styled.div`
  padding: 10px 0;
  width: 35%;
`

const InputGroupHeader: StyledComponent<Object, any, any> = styled.div`
  font-size: 14px;
  padding-bottom: 5px;
`

const AudienceWarningMessage: StyledComponent<Object, any, any> = styled.div`
  font-size: 14px;
  color: ${alertColor};
`

type Errors = Array<string>

type Props = {|
  t: Function,
  id: number,
  name: string,
  audienceIds: Array<number>,
  providers: Array<string>,
  conditions: Array<ConditionType>,
  action: ActionType,
  audienceOptions: Array<GroupedOptionType>,
  providerOptions: Array<OptionType>,
  actionOptions: Array<OptionType>,
  conditionOptions: Array<OptionType>,
  nameErrors: Errors,
  audienceIdsErrors: Errors,
  providersErrors: Errors,
  conditionsErrors: Array<Object>,
  actionErrors: Object,
  isLoading?: boolean,
  onClearErrors: Function,
  onChange: Function,
  onApply: Function
|}

export default class Form extends Component<Props> {
  render() {
    const {
      t,
      id,
      name,
      audienceIds,
      providers,
      conditions,
      action,
      isLoading,
      providerOptions,
      audienceOptions,
      actionOptions,
      conditionOptions,
      nameErrors,
      audienceIdsErrors,
      providersErrors,
      conditionsErrors,
      actionErrors,
      onClearErrors
    } = this.props

    return (
      <FormWrapper className="t-AutomatedRuleForm">
        <FormHeader>{t(id ? 'update_rule' : 'create_rule', { id: id })}</FormHeader>
        <InputGroupWrapper className="t-NameInput">
          <InputGroupHeader>{t('header.name')}</InputGroupHeader>
          <input
            value={name}
            placeholder={t('rule_name_placeholder')}
            disabled={isLoading}
            className="input-item input-item_full-width"
            onChange={(e) => {
              onClearErrors({ nameErrors: [] })
              this.ruleChanged({ name: e.target.value })
            }}
          />
          <ErrorText errors={nameErrors} />
        </InputGroupWrapper>
        <InputGroupWrapper className="t-ProviderInput__select">
          <InputGroupHeader>{t('header.providers')}</InputGroupHeader>
          <MultiSelect
            placeholder={t('select_providers')}
            value={providers}
            disabled={isLoading}
            options={providerOptions}
            onChange={(val) => {
              onClearErrors({ providersErrors: [] })
              this.ruleChanged({ providers: val })
            }}
          />
          <ErrorText errors={providersErrors} />
        </InputGroupWrapper>
        <InputGroupWrapper className="t-AudiencesInput__select">
          <InputGroupHeader>{t('header.audiences')}</InputGroupHeader>
          <AudienceWarningMessage>{t('audience_warning_message')}</AudienceWarningMessage>
          <MultiSelect
            placeholder={t('select_audiences')}
            disabled={isLoading}
            value={audienceIds}
            options={[]}
            groupedOptions={audienceOptions}
            onChange={(val) => {
              onClearErrors({ audienceIdsErrors: [] })
              this.ruleChanged({ audienceIds: val })
            }}
            selectAll
            noResultsText={t('no_audiences')}
          />
          <ErrorText errors={audienceIdsErrors} />
        </InputGroupWrapper>
        <Action
          t={t}
          action={action}
          options={actionOptions}
          disabled={isLoading}
          actionErrors={actionErrors}
          onChange={(action) => {
            onClearErrors({ actionErrors: [] })
            this.ruleChanged({ action })
          }}
        />
        <div>
          <InputGroupHeader>{t('header.conditions')}</InputGroupHeader>
          <Conditions
            t={t}
            conditions={conditions}
            options={conditionOptions}
            disabled={isLoading}
            conditionsErrors={conditionsErrors}
            onClearErrors={onClearErrors}
            onChange={(conditions) => this.ruleChanged({ conditions })}
          />
          {_.isString(conditionsErrors) && <ErrorText errors={conditionsErrors} />}
        </div>
        <InputGroupWrapper>
          <InputGroupHeader>{t('select_check_frequency')}</InputGroupHeader>
          <Select
            value={(action && action.check_frequency) || 'once_per_hour'}
            options={this.checkFrequencyOptions()}
            disabled={isLoading}
            onChange={(val) => this.checkFrequencyChanged(val)}
          />
          <ErrorText errors={actionErrors.check_frequency} />
        </InputGroupWrapper>
        <FloatPanel>{this.renderFloatPanel(id)}</FloatPanel>
      </FormWrapper>
    )
  }

  renderFloatPanel(id: number) {
    const { t } = this.props
    return (
      <div className="container">
        <button className="blue-button" onClick={() => this.apply()}>
          {t(id ? 'update' : 'apply')}
        </button>
      </div>
    )
  }

  ruleChanged(changes: Object) {
    this.props.onChange && this.props.onChange(changes)
  }

  apply() {
    this.props.onApply && this.props.onApply()
  }

  checkFrequencyOptions() {
    const { t } = this.props
    return [
      { id: 'once_per_hour', name: t('once_per_hour') },
      { id: 'once_per_day', name: t('once_per_day') }
    ]
  }

  checkFrequencyChanged(frequency: string) {
    const { action } = this.props
    let changedAction = _.extend({}, action, { check_frequency: frequency })
    this.ruleChanged({ action: changedAction })
  }
}
